/* istanbul ignore file */
import { useMutation, useQuery } from '@apollo/client';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { DecisionDefinitionModelQuery, DeployDecisionModelMutation } from '@css-xp/apd-common';
import {
  ErrorComponents,
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  useNotification,
} from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { decisionDefinitionModel, deployDecisionModel } from '../../queries/decisions';
import { DecisionControls } from './decision-controls';
import './decision-table.scss';
import TableMode from './table-mode';

interface DecisionTableProps {
  decisionDefinitionKey: string;
  reloadRuleHistory?: () => void;
  ssamContainer?: string;
}

const DecisionTable: FC<DecisionTableProps> = ({ decisionDefinitionKey, reloadRuleHistory, ssamContainer }) => {
  const user = useApplicationContext();
  const userId = user?.userId?.split('@')?.[0];
  const editor = useRef<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [dmnLibraryError, setDmnLibraryError] = useState<Error | undefined>(undefined);
  const { data, loading, error, refetch: refetchModel } = useQuery<DecisionDefinitionModelQuery>(
    decisionDefinitionModel,
    {
      skip: !decisionDefinitionKey || !user,
      variables: {
        decisionDefinitionKey,
        id: userId,
      },
    }
  );
  const [updateXML, { loading: updating }] = useMutation<DeployDecisionModelMutation>(deployDecisionModel);

  const { sendErrorNotification, setNotificationSent, sendSuccessNotification } = useNotification();

  const xml = useMemo(() => {
    return data?.decisionDefinitionModel ?? '';
  }, [data?.decisionDefinitionModel]);

  const handleEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const handleCancel = useCallback(() => {
    setEditMode(false);
  }, []);

  const renderErrorNotification = useCallback((message: string) => {
    const notificationOptions = errorNotificationBuilder()
      .withId('save-error')
      .withHeader(`We're unable to apply the changes because:`)
      .withMessage(
        <>
          <div className="decision-table-error-reason">
            <ul>
              <li>{message}</li>
            </ul>
          </div>
          <div>
            Still having trouble? Contact the <ErrorComponents.ChatRoomLink />
          </div>
        </>
      )
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendErrorNotification(notificationOptions);
    setNotificationSent(true);
  }, []);

  const renderSuccessNotification = useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .withId('save-success')
      .withHeader(`Edits applied`)
      .withMessage(`You've changed the selected rules.`)
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (editor.current) {
      let xml;
      try {
        xml = await editor.current.getXml();
      } catch (err) {
        setDmnLibraryError(err as Error);
        return;
      }
      try {
        const result = await updateXML({
          variables: {
            id: userId,
            decisionDefinitionKey,
            xml,
          },
        });
        const id = result?.data?.deployDecisionModel?.id;
        if (id) {
          renderSuccessNotification();
          setEditMode(false);
          refetchModel();
          reloadRuleHistory?.();
          return;
        }
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        renderErrorNotification(err?.message);
      }
    }
  }, [decisionDefinitionKey, refetchModel, setNotificationSent, updateXML, userId]);

  const err: Error | undefined = useMemo(() => {
    return error || dmnLibraryError;
  }, [error, dmnLibraryError]);

  const id = useMemo(() => {
    return `ddk-${decisionDefinitionKey}`;
  }, [decisionDefinitionKey]);

  useEffect(() => {
    if (editMode) {
      setEditMode(false);
    }
  }, [decisionDefinitionKey]);

  if (loading) return <LoadingIndicator />;

  if (err) return <ErrorComponents.GenericErrorPage error={err} />;

  return (
    <>
      <div className="header-actions">
        <DecisionControls
          editMode={editMode}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={updating}
          ssamContainer={ssamContainer}
        />
      </div>
      <div className="decision-table">
        <TableMode xml={xml} ref={editor} setDmnLibraryError={setDmnLibraryError} id={id} editMode={editMode} />
      </div>
    </>
  );
};

export default DecisionTable;
